import './OrderPage.scss';
import { useTranslation } from 'react-i18next';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Fade, Grid, Step, Stepper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { Gift } from 'react-feather';
import { includes } from 'lodash';
import DetailsStep from './steps/DetailsStep';
import { useOrderPage } from './orderPageContext/useOrderPage';
import PersonalDetailsStep from './steps/PersonalDetailsStep';
import DateAndTimeStep from './steps/dateAndTimeStep/DateAndTimeStep';
import {
  CodesService,
  OrderPageData,
  OrderService,
  PaymentFormType,
  PaymentsService,
  VoucherService,
  VoucherStatus,
} from '../api';
import PaymentStep from './steps/PaymentStep';
import { useLanguage } from '../common/GeneralUtils';
import OrderUtils, { calculatePrice } from './OrderUtils';
import ConfirmationDialog from '../common/ui/ConfimationDialog';
import MonkeybookWidgetFooter from '../common/ui/MonkeybookWidgetFooter';
import VoucherSummary from './steps/VoucherSummary';
import VoucherRedemptionSummary from './steps/VoucherRedemptionSummary';
import PluginUtils, { getPluginData, PluginType } from './PluginUtils';
import ErrorBox from './steps/ErrorBox';
import { OrderPageTypography } from './themedComponents/OrderPageTypography';
import { OrderPageButton } from './themedComponents/OrderPageButton';
import { OrderPageError } from './themedComponents/OrderPageError';
import { SpecificEventSummary, StyledImage } from './steps/SpecificEventSummary';
import { MultiLocationDetails } from './MultiLocationDetails';
import { OrderPageDivider } from './themedComponents/OrderPageDivider';
import { PriceSummary } from './PriceSummary';
import { OrderPageIntent } from './OrderPageIntent';
import { getUtmDataFromLocalStorage } from './orderCompletion/utmLocalStorage';
import { OrderStepConnector, OrderStepIcon, OrderStepLabel } from './CustomStepper';
import {
  clearOrderPageStateFromLocalStorage,
  getOrderPageStateTTLLeft,
} from './orderPageContext/orderPageStateLocalStorage';
import {
  getDisclaimersStepNumber,
  getPaymentStepNumber,
  getPersonalDetailsStepNumber,
  shouldShowDisclaimersStep,
} from './utils/stepHelpers';
import DisclaimersStep from './steps/DisclaimersStep';

export type PersonalDetails = {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  custom_personal_details?: Record<string, number | string | boolean>;
  referral_source?: string;
  voucher_greeting_message?: string;
  recipient_email?: string;
  delivery_date?: string | null;
  delivery_time?: string | null;
  should_send_delayed?: boolean;
};

// this is used in case voucher is not valid for weekend and being redeemed
const calculateShouldHideWeekends = (orderPageState: any) => {
  // we care only if orderpage has weekend  plugin
  const pluginData = getPluginData(orderPageState.orderPage.plugins, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS);
  if (!pluginData) {
    return false;
  }
  const customDetails = orderPageState.voucher?.custom_details || orderPageState.details.custom_details;
  return (
    !PluginUtils.customDetailsHasPlugin(customDetails, PluginType.WEEKEND_AND_ISRAELI_HOLIDAYS) &&
    orderPageState.orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD
  );
};

enum OrderPageErrors {
  DATE_AND_TIME_NOT_SELECTED = 'dateAndTimeNotSelected',
  SAVE_ORDER_EXCEPTION = 'saveOrderException',
  PHONE_NUMBER_SAVE_ORDER_EXCEPTION = 'phoneNumberSaveOrderException',
  AT_LEAST_ONE_PERSON = 'atLeastOnePerson',
}

const OrderPage = () => {
  const theme = useTheme();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    reloadDates,
    orderPageState,
    setOrderPageState,
    setActiveStep,
    setOrderCustomDetails,
    setActiveStepAndOrderPageIntent,
    setTime,
  } = useOrderPage();
  const formRef = useRef();
  const [detailsStepErrors] = useState({});
  const [error, setError] = useState<OrderPageErrors | undefined>(undefined);
  const [activeStep, setLocalActiveStep] = useState<number>(orderPageState?.activeStep || 0);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const specificEvent = !!orderPageState?.specificEvent;
  const orderPageLanguages = orderPageState?.account.customer_facing_widgets_languages;
  const [shouldDiasbleNext, setShouldDiasbleNext] = useState(false);
  const [disclaimersAccepted, setDisclaimersAccepted] = useState(false);
  const [disclaimersError, setDisclaimersError] = useState(false);
  const language = useLanguage();
  const posthog = usePostHog();
  const { voucher, voucherError, orderPageIntent, orderPage, specificOrderPage, specificEventError } = orderPageState;
  const personalDetailsRef = useRef<HTMLDivElement | null>(null);
  const persons = orderPageState.details?.persons || 1;
  const shouldShowVoucherFlow = voucher && voucher.status === VoucherStatus.ACTIVE;
  const [filteredAvailableDates, setFilteredAvailableDates] = useState(orderPageState.availableDates);
  const [sessionTtlMilliseconds, setSessionTtlMilliseconds] = useState<number | null>(null);
  const queryParams = new URLSearchParams(window.location.search);
  const embeddedWidget = queryParams.get('embed') === 'true';
  const hideTitle = !!queryParams.get('hidetitle');
  const waitingListUrl = `/waiting-list/${orderPageState.account.id}/${orderPageState.experience.id}/${orderPage.id}/${
    orderPageState.details?.persons || 1
  }`;
  const waitListEnabled = orderPageState.orderPage.waiting_list && orderPageState.orderPage.waiting_list.enabled;
  useEffect(() => {
    setFilteredAvailableDates(orderPageState.availableDates);
  }, [orderPageState.availableDates]);

  const elementRef = useCallback((node: any) => {
    if (!node) return;
    const resizeObserver = new ResizeObserver(() => {
      const iframeHeight = document.body.offsetHeight;
      window.parent.postMessage({ height: iframeHeight }, '*');
    });
    resizeObserver.observe(node);
  }, []);

  let sessionTimeout: NodeJS.Timeout | null = null;

  const shouldHideWeekends = calculateShouldHideWeekends(orderPageState);
  const hasDisclaimersStep = shouldShowDisclaimersStep(orderPage, language);
  const personalDetailsStep = getPersonalDetailsStepNumber();
  const disclaimersStep = getDisclaimersStepNumber();
  const paymentStep = getPaymentStepNumber(orderPage, language);

  const initSessionExpiredTimeout = (ttl?: number) => {
    if (!ttl) {
      ttl = OrderUtils.ORDER_PAGE_TTL * 1000;
    }

    setSessionTtlMilliseconds(ttl);
    sessionTimeout = setTimeout(() => {
      // todo make it nicer then alert.....
      // eslint-disable-next-line no-alert
      alert('Session expired. please reload');
      clearOrderPageStateFromLocalStorage();
      window.location.reload();
    }, ttl);
  };

  useEffect(() => {
    void (() => {
      if (orderPageState.activeStep === getPaymentStepNumber(orderPageState.orderPage, language)) {
        const ttl = getOrderPageStateTTLLeft(orderPageState.orderPage.id);
        if (ttl > 0) {
          initSessionExpiredTimeout(ttl);
        }
      }
    })();
  }, []);

  const moveToNextStep = (numberOfSteps = 1, intent?: OrderPageIntent) => {
    setLoading(false);
    if (intent) {
      setActiveStepAndOrderPageIntent(activeStep + numberOfSteps, intent);
    } else {
      setActiveStep(activeStep + numberOfSteps);
    }

    setLocalActiveStep((prevActiveStep) => prevActiveStep + numberOfSteps);
  };

  const handleDetailsDateTimeSelectionNext = (activeStep: number, intent?: OrderPageIntent) => {
    if (Object.keys(detailsStepErrors).length > 0) {
      return;
    }
    if (orderPageState.details.persons < 1) {
      setError(OrderPageErrors.AT_LEAST_ONE_PERSON);
      return;
    }
    if (
      intent !== OrderPageIntent.GIFT_CARD &&
      (orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD || activeStep === 1) &&
      (!orderPageState.dateAndTimeDetails.date || !orderPageState.dateAndTimeDetails.time)
    ) {
      setError(OrderPageErrors.DATE_AND_TIME_NOT_SELECTED);
    } else if (orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD) {
      moveToNextStep(2, intent);
    } else {
      moveToNextStep(1, intent);
    }
  };

  const getPaymentForm = async (order: any) => {
    return PaymentsService.getPaymentForm({
      language,
      account_id: orderPageState.account.id,
      experience_id: orderPageState.experience.id,
      order_page_id: orderPageState.orderPage.id,
      entity_id: order.id,
      payment_form_type:
        orderPageIntent === OrderPageIntent.GIFT_CARD ? PaymentFormType.VOUCHER : PaymentFormType.ORDER,
    });
  };
  const clearSessionExpiredTimeout = () => {
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
    }
  };
  const handleReset = () => {
    clearOrderPageStateFromLocalStorage();
    clearSessionExpiredTimeout();
    window.location.reload();
  };
  const saveOrder = async (personalDetails?: any) => {
    // Get current session ID dynamically at save time
    const currentQueryParams = new URLSearchParams(window.location.search);
    const currentSessionId = currentQueryParams.get('session_id') || undefined;

    // Report to PostHog when there's no session

    if (orderPageIntent === OrderPageIntent.GIFT_CARD) {
      const voucher = await VoucherService.createVoucher({
        language,
        ...(personalDetails || orderPageState.personalDetails),
        account_id: orderPageState.account.id,
        experience_id: orderPageState.experience.id,
        order_page_id: orderPageState.orderPage.id,
        ...orderPageState.details,
        can_spam: orderPageState.canSpam,
        utm_data: getUtmDataFromLocalStorage(),
        session_id: currentSessionId,
        from_embedded_widget: embeddedWidget,
      });
      if (!currentSessionId && embeddedWidget) {
        posthog.capture('Order Saved Without Session', {
          orderPageId: orderPageState.orderPage.id,
          accountId: orderPageState.account.id,
          orderPageIntent,
          voucherId: voucher?.id,
        });
      }
      return voucher;
    }
    try {
      const orderDetails = { ...orderPageState.details };

      if (orderPageState.account.deposit_percentage) {
        orderDetails.price = calculatePrice(
          orderPageState.orderPage,
          orderPageState.details,
          orderPageState.dateAndTimeDetails,
          undefined,
          orderPageState.account.deposit_percentage,
        );
      }

      const order = await OrderService.saveOrder({
        language,
        account_id: orderPageState.account.id,
        experience_id: orderPageState.experience.id,
        order_page_id: orderPageState.orderPage.id,
        details: orderDetails,
        date_and_time_details: orderPageState.dateAndTimeDetails,
        personal_details: personalDetails || orderPageState.personalDetails,
        can_spam: orderPageState.canSpam,
        utm_data: getUtmDataFromLocalStorage(),
        session_id: currentSessionId,
        from_embedded_widget: embeddedWidget,
      });
      if (orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD) {
        await CodesService.applyCode({
          entity_id: order.id,
          code: voucher!.code,
        });
      }
      if (!currentSessionId && embeddedWidget) {
        posthog.capture('Order Saved Without Session', {
          orderPageId: orderPageState.orderPage.id,
          accountId: orderPageState.account.id,
          orderPageIntent,
          orderId: order?.id,
        });
      }
      return order;
    } catch (e) {
      console.error(e);
      setError(OrderPageErrors.SAVE_ORDER_EXCEPTION);
      clearOrderPageStateFromLocalStorage();
      clearSessionExpiredTimeout();
      setSessionTtlMilliseconds(null);
      setActiveStep(0);
      setLocalActiveStep(0);
      reloadDates();
      setLoading(false);
    }
    return undefined;
  };

  const handlePersonalDetailsNext = async () => {
    const data = formRef;
    if (!data.current) {
      return;
    }
    // @ts-ignore
    await data.current.submitForm();
    const { isValid } = data.current;
    if (isValid) {
      setLoading(true);
      // @ts-ignore
      const values = data.current.values as PersonalDetails;
      const personalDetails = {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phone: values.phone.replace('-', ''),
        custom_personal_details: values.custom_personal_details,
        referral_source: values.referral_source,
        voucher_greeting_message: values.voucher_greeting_message,
        recipient_email: values.recipient_email,
        delivery_date: values.delivery_date,
        delivery_time: values.delivery_time,
        should_send_delayed:
          !!values.recipient_email &&
          values.should_send_delayed &&
          Boolean(values.delivery_date && values.delivery_time),
      };
      console.log(personalDetails);
      let url = '';
      let order = null;
      if (orderPageState.account.has_payment_integration) {
        order = await saveOrder(personalDetails);
        posthog.capture('Saved Order', {
          orderPageId: orderPageState.orderPage.id,
          accountId: orderPageState.account.id,
        });
        if (orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD) {
          if (orderPageIntent === OrderPageIntent.BUY_NOW) {
            initSessionExpiredTimeout(); // no timeouts in guftcards as it's not blocking calendar.
          }
          try {
            if (!hasDisclaimersStep) {
              url = await getPaymentForm(order);
            }
          } catch (e: any) {
            console.error(e);
            setError(
              e?.body?.includes('717')
                ? OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION
                : OrderPageErrors.SAVE_ORDER_EXCEPTION,
            );
            clearOrderPageStateFromLocalStorage();
            clearSessionExpiredTimeout();
            setSessionTtlMilliseconds(null);
            setActiveStep(0);
            setLocalActiveStep(0);
            reloadDates();
            setLoading(false);
            return;
          }
        }
      }
      // If we have disclaimers, move to disclaimers step without payment processing
      // If no disclaimers, move to payment step with payment processing (current behavior)
      if (hasDisclaimersStep) {
        setOrderPageState({
          ...orderPageState,
          activeStep: activeStep + 1,
          personalDetails,
          order_id: order?.id,
          returningCustomer: order?.returning_customer || false,
        });
      } else {
        setOrderPageState({
          ...orderPageState,
          activeStep: activeStep + 1,
          personalDetails,
          paymentUrl: url,
          order_id: order?.id,
          returningCustomer: order?.returning_customer || false,
        });
      }
      setLoading(false);
      setLocalActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleDisclaimersNext = async () => {
    if (!disclaimersAccepted) {
      setDisclaimersError(true);
      return;
    }

    setDisclaimersError(false);

    setLoading(true);
    let url = '';

    // Generate payment URL since we skipped it in handlePersonalDetailsNext
    if (orderPageState.account.has_payment_integration && orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD) {
      if (orderPageIntent === OrderPageIntent.BUY_NOW) {
        initSessionExpiredTimeout(); // no timeouts in giftcards as it's not blocking calendar.
      }
      try {
        url = await getPaymentForm({ id: orderPageState.order_id });
      } catch (e: any) {
        console.error(e);
        setError(
          e?.body?.includes('717')
            ? OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION
            : OrderPageErrors.SAVE_ORDER_EXCEPTION,
        );
        clearOrderPageStateFromLocalStorage();
        clearSessionExpiredTimeout();
        setSessionTtlMilliseconds(null);
        setActiveStep(0);
        setLocalActiveStep(0);
        reloadDates();
        setLoading(false);
        return;
      }
    }

    setOrderPageState({
      ...orderPageState,
      activeStep: activeStep + 1,
      paymentUrl: url,
    });
    setLoading(false);
    setLocalActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePaymentFormNext = async () => {
    setLoading(true);
    await saveOrder();
    navigate(
      `/order/completed?order/completed?account_id=${orderPageState.account.id}&order_page_id=${orderPageState.orderPage.id}&transaction_type=${orderPageIntent}&entity_id=${orderPageState.order_id}`,
    );
    setLoading(false);
  };
  const handleNext = async (intent?: OrderPageIntent) => {
    window.parent.postMessage({ name: 'monkeybook_scrollToTop' }, '*');
    if (activeStep === 0 || activeStep === 1) {
      if (personalDetailsRef.current) {
        personalDetailsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      handleDetailsDateTimeSelectionNext(activeStep, intent);
      setTimeout(() => {
        window.parent.postMessage({ name: 'monkeybook_scrollToTop' }, '*');
      }, 250);
    } else if (activeStep === personalDetailsStep) {
      await handlePersonalDetailsNext();
    } else if (hasDisclaimersStep && activeStep === disclaimersStep) {
      await handleDisclaimersNext();
    } else if (activeStep === paymentStep) {
      await handlePaymentFormNext();
    }
  };

  const handleBack = () => {
    if (orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD) {
      setActiveStepAndOrderPageIntent(activeStep - 1, OrderPageIntent.REDEEM_GIFT_CARD);
      setLocalActiveStep((prevActiveStep) => prevActiveStep - 1);
    } else if (activeStep === personalDetailsStep) {
      // reset intent as we wont know if they want a voucher or what
      setActiveStepAndOrderPageIntent(activeStep - 1, OrderPageIntent.BUY_NOW);
      setLocalActiveStep((prevActiveStep) => prevActiveStep - 2);
    } else if (hasDisclaimersStep && activeStep === disclaimersStep) {
      // Cannot go back from disclaimers step - order is pending and timer is running
      // Show reset dialog instead (handled by shouldShowStartOverButton)
      setIsResetDialogOpen(true);
    } else if (activeStep === paymentStep) {
      // Going back from payment step
      if (hasDisclaimersStep) {
        // Cannot go back when disclaimers were shown - order is pending
        setIsResetDialogOpen(true);
      } else {
        // Go back to details/date selection (skip personal details)
        setActiveStep(activeStep - 2);
        setLocalActiveStep((prevActiveStep) => prevActiveStep - 2);
      }
    } else {
      setActiveStep(activeStep - 2);
      setLocalActiveStep((prevActiveStep) => prevActiveStep - 2);
    }
  };

  const { date } = orderPageState.dateAndTimeDetails;
  const nextButtonText = () => {
    switch (activeStep) {
      case 0:
        return t('next');
      case 1:
        return t('next');
      case 2:
        return orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD ? t('redeem') : t('next');
      default:
        if (hasDisclaimersStep && activeStep === disclaimersStep) {
          return t('order_page.accept_disclaimers');
        }
        if (activeStep === paymentStep) {
          return t('finish');
        }
        return t('next');
    }
  };

  if (voucherError) {
    return (
      <ErrorBox
        account={orderPageState.account}
        orderPageLabel={orderPageState.orderPage.label[language]}
        error={voucherError}
      />
    );
  }
  if (specificEventError) {
    return (
      <ErrorBox
        account={orderPageState.account}
        orderPageLabel={orderPageState.orderPage.label[language]}
        error={specificEventError}
        date={orderPageState.dateAndTimeDetails?.date}
        waitingListUrl={waitingListUrl}
        waitListEnabled={waitListEnabled}
      />
    );
  }
  const shouldHideExtraPriceSummary =
    !isSmallScreen || orderPageState.specificOrderPage?.onlyVouchers || activeStep > 0;
  const shouldShowStartOverButton =
    (activeStep === paymentStep || (hasDisclaimersStep && activeStep === disclaimersStep)) &&
    orderPageState.account.has_payment_integration &&
    orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD;
  const shouldHideLocationAndDateTimeStep =
    activeStep > 1 ||
    (orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD && activeStep !== 1) ||
    specificOrderPage?.onlyVouchers ||
    specificEvent;
  return (
    <Grid
      container
      display="flex"
      justifyContent="space-around"
      sx={{ '::-webkit-scrollbar': { display: 'none' } }}
      style={{
        height: '100%',
        width: '100%',
      }}
      ref={personalDetailsRef}>
      <Grid
        ref={elementRef}
        container
        flexDirection="column"
        style={{
          borderRadius: '12px',
          background: theme.customTheme.palette.background,
          height: '100%',
          width: '100%',
          maxWidth: '710px',
        }}
        className="order-page-wrapper">
        {orderPageState.account.name === 'Speculo' && (
          <>
            <script async src="https://www.googletagmanager.com/gtag/js?id=UA-148720460-1" />
            <script src="/scripts/speculo-gtag.js" />
            <script src="/scripts/speculo-facebook.js" />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                src="https://www.facebook.com/tr?id=935698493466004&ev=PageView&noscript=1"
                alt="facebook link"
              />
            </noscript>
          </>
        )}
        {orderPageState.account.id === '64e246f85f3e1733728cb75f' && (
          <>
            <script async src="https://www.googletagmanager.com/gtag/js?id=G-SZ6K3X7MKK" />
            <script src="/scripts/yaar-gtag.js" />
            <script src="/scripts/yaar-facebook.js" />
            <noscript>
              <img
                height="1"
                width="1"
                style={{ display: 'none' }}
                src="https://www.facebook.com/tr?id=935698493466004&ev=PageView&noscript=1"
                alt="facebook link"
              />
            </noscript>
          </>
        )}

        <Grid
          px={isSmallScreen ? 2 : 4}
          container
          flexDirection="column"
          className={`order-page-box${activeStep !== 0 ? ' order-page-box-padding' : ''}`}
          style={{
            maxWidth: '650px',
            color: theme.customTheme.palette.text.primary,
            border: `2px solid ${theme.customTheme.palette.border}`,
          }}
          boxShadow={4}>
          {shouldShowStartOverButton && (
            <Box
              sx={{ justifyContent: 'flex-start', display: 'flex' }}
              onClick={() => setIsResetDialogOpen(true)}
              mb={2}>
              <OrderPageTypography
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: 'text.secondary',
                }}>
                {t('reset')}
              </OrderPageTypography>
            </Box>
          )}
          {!embeddedWidget && (
            <Grid container justifyContent="center" mb={2}>
              <img width="200" src={orderPageState.account?.logo} alt="Account logo" />
            </Grid>
          )}
          {!hideTitle && (
            <Grid container justifyContent="center">
              <OrderPageTypography variant="h1" style={{ textAlign: 'center', width: '100%' }}>
                {orderPageState.orderPage.label[language]}
              </OrderPageTypography>
            </Grid>
          )}
          <Grid container mt={3} mb={1}>
            <Grid container hidden={isSmallScreen} display={isSmallScreen ? 'none' : 'flex'} flexDirection="column">
              <Stepper activeStep={activeStep} alternativeLabel connector={<OrderStepConnector />}>
                <Step>
                  <OrderStepLabel StepIconComponent={OrderStepIcon}>
                    <Grid mt={-1.4}>
                      <OrderPageTypography>{t('order_page.step1')}</OrderPageTypography>
                    </Grid>
                  </OrderStepLabel>
                </Step>
                {orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD && (
                  <Step>
                    <OrderStepLabel StepIconComponent={OrderStepIcon}>
                      <Grid mt={-1.4}>
                        <OrderPageTypography>{t('order_page.step2')}</OrderPageTypography>
                      </Grid>
                    </OrderStepLabel>
                  </Step>
                )}
                <Step>
                  <OrderStepLabel StepIconComponent={OrderStepIcon}>
                    <Grid mt={-1.4}>
                      <OrderPageTypography>{t('order_page.step3')}</OrderPageTypography>
                    </Grid>
                  </OrderStepLabel>
                </Step>
                {hasDisclaimersStep && (
                  <Step>
                    <OrderStepLabel StepIconComponent={OrderStepIcon}>
                      <Grid mt={-1.4}>
                        <OrderPageTypography>{t('order_page.step_disclaimers')}</OrderPageTypography>
                      </Grid>
                    </OrderStepLabel>
                  </Step>
                )}
                <Step>
                  <OrderStepLabel StepIconComponent={OrderStepIcon}>
                    <Grid mt={-1.4}>
                      <OrderPageTypography>{t('order_page.step4')}</OrderPageTypography>
                    </Grid>
                  </OrderStepLabel>
                </Step>
              </Stepper>
            </Grid>
            <OrderPageError
              isShowingError={includes(
                [
                  OrderPageErrors.SAVE_ORDER_EXCEPTION,
                  OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION,
                  OrderPageErrors.AT_LEAST_ONE_PERSON,
                ],
                error,
              )}
              message={
                error === OrderPageErrors.PHONE_NUMBER_SAVE_ORDER_EXCEPTION
                  ? t('orderPageErrors.saveExceptionNonIsraeliPhoneNumber')
                  : error === OrderPageErrors.AT_LEAST_ONE_PERSON
                  ? t('orderPageErrors.atLeastOnePerson')
                  : t('orderPageErrors.saveException')
              }
              setClearError={() => setError(undefined)}
            />
            {!embeddedWidget && activeStep === 0 && (
              <Grid container flexDirection="column" mt={4} gap={1} flexWrap="nowrap" alignItems="center">
                {orderPageState.orderPage?.photo && (
                  <Grid item xs={isSmallScreen ? 12 : 8}>
                    <StyledImage
                      src={orderPageState.orderPage.photo}
                      alt="Experience cover"
                      style={{ border: `1px solid ${theme.customTheme.palette.border}` }}
                    />
                  </Grid>
                )}
                <Typography variant={isSmallScreen ? undefined : 'h5'} fontWeight="800">
                  {orderPageState.orderPage.description?.[language]}
                </Typography>
              </Grid>
            )}
            {specificEvent && activeStep === 0 && (
              <SpecificEventSummary specificEvent={orderPageState.specificEvent!} />
            )}
            <Grid container wrap="nowrap" mt={activeStep !== 0 ? 0 : isSmallScreen ? 1 : 3} flexDirection="column">
              <Grid item hidden={activeStep !== 0}>
                {shouldShowVoucherFlow ? (
                  <VoucherSummary voucher={voucher} />
                ) : (
                  <DetailsStep errors={detailsStepErrors} />
                )}
              </Grid>
              {!shouldHideExtraPriceSummary && (
                <Grid item mt={1}>
                  <PriceSummary
                    orderPageState={orderPageState}
                    activeStep={activeStep}
                    persons={persons}
                    date={date}
                    orderPageIntent={orderPageIntent}
                  />
                </Grid>
              )}
              {!shouldHideLocationAndDateTimeStep && (
                <Grid>
                  <MultiLocationDetails
                    defaultLocationId={orderPage.location_id}
                    account={orderPageState.account}
                    initialAvailableDates={orderPageState.availableDates}
                    isLoadingDates={orderPageState.isLoadingDates}
                    setFilteredAvailableDates={setFilteredAvailableDates}
                  />
                  <DateAndTimeStep
                    account={orderPageState.account}
                    initialAvailableDates={orderPageState.availableDates}
                    locations={orderPageState.account.locations}
                    setShouldDisableNext={setShouldDiasbleNext}
                    availableDates={filteredAvailableDates}
                    setTime={setTime}
                    shouldHideWeekends={shouldHideWeekends}
                    orderPageIntent={orderPageIntent}
                    orderPage={orderPage}
                    date={orderPageState.dateAndTimeDetails.date}
                    time={orderPageState.dateAndTimeDetails.time}
                    isLoadingDates={orderPageState.isLoadingDates}
                    waitingListUrl={waitingListUrl}
                    orderDetails={orderPageState.details}
                    setDateTimeAndPrice={({
                      newDate,
                      newTime,
                      newPrice,
                    }: {
                      newDate?: string | null;
                      newTime?: number | null;
                      newPrice?: number;
                    }) =>
                      setOrderPageState({
                        ...orderPageState,
                        dateAndTimeDetails: {
                          date: newDate,
                          time: newTime,
                        },
                        details: {
                          ...orderPageState.details,
                          price: newPrice || orderPageState.details.price,
                        },
                      })
                    }
                    noMinDate={specificOrderPage?.noMinDate}
                    troubleshoot={specificOrderPage?.troubleshoot}
                    specificEvent={specificEvent}
                    preSelectedDate={specificOrderPage?.preSelectedDate}
                  />
                </Grid>
              )}

              <Grid item hidden={activeStep !== 2}>
                <PersonalDetailsStep
                  formRef={formRef}
                  orderPage={orderPageState.orderPage}
                  setOrderCustomDetails={setOrderCustomDetails}
                  orderPageIntent={orderPageIntent}
                  holidayCalendar={orderPageState.account.holiday_calendar}
                />
              </Grid>

              {hasDisclaimersStep && (
                <Grid item hidden={activeStep !== disclaimersStep}>
                  <DisclaimersStep
                    orderPage={orderPage}
                    onAcceptanceChange={(accepted) => {
                      setDisclaimersAccepted(accepted);
                      if (accepted) {
                        setDisclaimersError(false);
                      }
                    }}
                    isAccepted={disclaimersAccepted}
                    showError={disclaimersError}
                  />
                </Grid>
              )}
              {specificOrderPage?.onlyVouchers &&
                activeStep === 0 &&
                (orderPage as OrderPageData).vouchers_config?.voucher_widget_title && (
                  <Grid container flexDirection="column">
                    <OrderPageTypography variant={'h3'}>
                      {(orderPage as OrderPageData).vouchers_config?.voucher_widget_title?.[language]}
                    </OrderPageTypography>
                    <OrderPageTypography variant={'h6'}>
                      {(orderPage as OrderPageData).vouchers_config?.voucher_widget_subtitle?.[language]}
                    </OrderPageTypography>
                  </Grid>
                )}

              {activeStep === paymentStep && orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD && (
                <PaymentStep orderPageIntent={orderPageIntent} sessionTtlMilliseconds={sessionTtlMilliseconds} />
              )}
              {activeStep === paymentStep && orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD && (
                <VoucherRedemptionSummary />
              )}
            </Grid>
            <Grid container className="middle-box" mb={2} justifyContent="center">
              {error === OrderPageErrors.DATE_AND_TIME_NOT_SELECTED &&
                activeStep < 2 &&
                (!orderPageState.dateAndTimeDetails.date || !orderPageState.dateAndTimeDetails.time) && (
                  <Fade in timeout={1000}>
                    <Alert sx={{ width: '230px', marginTop: '16px' }} severity="error">
                      <OrderPageTypography color="error">{t('order_page.date_error')}</OrderPageTypography>
                    </Alert>
                  </Fade>
                )}
            </Grid>
            {activeStep !== paymentStep && orderPageState.account.has_payment_integration && (
              <Grid
                container
                px={0.5}
                mx={activeStep !== 0 && !isSmallScreen ? 4 : 0}
                className="footer-container"
                display="flex"
                justifyContent="space-between"
                direction={isSmallScreen && activeStep === 0 ? 'column' : 'row'}>
                <Grid item>
                  {shouldShowStartOverButton ? (
                    <OrderPageButton variant="outlined" color="inherit" onClick={() => setIsResetDialogOpen(true)}>
                      <OrderPageTypography color="button.outlined">{t('reset')}</OrderPageTypography>
                    </OrderPageButton>
                  ) : (
                    activeStep !== 0 &&
                    !(hasDisclaimersStep && activeStep >= disclaimersStep) && (
                      <OrderPageButton
                        variant="outlined"
                        color="inherit"
                        disabled={activeStep === 0}
                        onClick={handleBack}>
                        <OrderPageTypography color="button.outlined">{t('back')}</OrderPageTypography>
                      </OrderPageButton>
                    )
                  )}
                </Grid>

                <PriceSummary
                  orderPageState={orderPageState}
                  activeStep={activeStep}
                  persons={persons}
                  date={date}
                  orderPageIntent={orderPageIntent}
                />

                <Grid
                  item
                  xs={isSmallScreen ? undefined : 6}
                  flexDirection="column"
                  display={specificOrderPage?.onlyVouchers && activeStep === 0 ? 'none' : 'flex'}
                  alignItems="flex-end">
                  <OrderPageButton
                    disabled={orderPageState.isLoadingDates || shouldDiasbleNext || specificOrderPage?.troubleshoot}
                    loadingButton
                    sx={{
                      width: '150px',
                    }}
                    loading={loading}
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      posthog.startSessionRecording({});
                      posthog.capture('Button clicked', {
                        name: 'Start order',
                        orderPageId: orderPageState.orderPage.id,
                        accountId: orderPageState.account.id,
                      });
                      void handleNext();
                    }}
                    className="next-button">
                    <OrderPageTypography variant="h6" color="button.contained" bold>
                      {nextButtonText()}
                    </OrderPageTypography>
                  </OrderPageButton>
                </Grid>
              </Grid>
            )}
          </Grid>
          {activeStep === 0 && orderPageIntent !== OrderPageIntent.REDEEM_GIFT_CARD && !specificEvent && (
            <Grid container flexDirection="column" gap={2} my={3}>
              {!specificOrderPage?.onlyVouchers && (
                <OrderPageDivider>
                  <OrderPageTypography>{t('order_page.divider')}</OrderPageTypography>
                </OrderPageDivider>
              )}
              <Grid
                item
                display="flex"
                justifyContent="space-around"
                flexDirection="column"
                alignItems="center"
                gap={2}>
                {!specificOrderPage?.onlyVouchers && <OrderPageTypography>{t('vouchersremark')}</OrderPageTypography>}
                <OrderPageButton
                  sx={{
                    width: '150px',
                  }}
                  startIcon={<Gift size={16} color={theme.customTheme.palette.text.button.outlined} />}
                  variant="outlined"
                  onClick={() => {
                    posthog.startSessionRecording({});
                    posthog.capture('Button clicked', {
                      name: 'Start voucher',
                      orderPageId: orderPageState.orderPage.id,
                    });
                    void handleNext(OrderPageIntent.GIFT_CARD);
                  }}
                  className="next-button"
                  disabled={specificOrderPage?.troubleshoot || orderPageState.isLoadingDates}>
                  <OrderPageTypography variant="h6" color="button.outlined">
                    {specificOrderPage?.onlyVouchers ? t('purchaseVoucher') : t('purchasegift')}
                  </OrderPageTypography>
                </OrderPageButton>
              </Grid>
            </Grid>
          )}
        </Grid>

        <MonkeybookWidgetFooter languages={orderPageLanguages} accountId={orderPageState.account.id} />

        <ConfirmationDialog
          title={t('order_page.reset_dialog.title')}
          ctaText={t('reset')}
          isOpen={isResetDialogOpen}
          onClose={(actionConfirmed: any) => {
            setIsResetDialogOpen(false);
            if (actionConfirmed) {
              handleReset();
            }
          }}
        />
      </Grid>
    </Grid>
  );
};

export default OrderPage;
